import { useEffect, useState } from "react";
import { Activity, ShieldAlert } from "lucide-react";
import api from "../../lib/api";

/**
 * Brief 3.13 (Feb 25 2026) — Brain-health pill.
 *
 * Reads `GET /admin/nerea/brain-health` (sourced from `nerea_outage_state`)
 * and renders a 12-px pill in the admin sidebar:
 *   • green "Brain healthy"     when no open alerts
 *   • amber "Brain degraded"    when ≥ 1 open outage row; hover to see
 *                                 the kinds + duration since first alert.
 *
 * Polls every 60s. Doesn't block render — if the endpoint is slow or
 * unavailable, the pill simply doesn't appear (no console spam).
 */
export default function BrainHealthPill() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const { data: body } = await api.get("/admin/nerea/brain-health");
        if (alive) setData(body);
      } catch { /* swallow — pill stays hidden */ }
    };
    tick();
    timer = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  if (!data) return null;
  if (data.healthy) {
    return (
      <div
        data-testid="brain-health-pill-healthy"
        title="Nerea brain reachable and answering known-good questions."
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/[0.05] text-emerald-200 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.16em] mb-4"
      >
        <Activity className="h-2.5 w-2.5" />
        Brain healthy
      </div>
    );
  }
  const since = data.degraded_since
    ? new Date(data.degraded_since).toLocaleString()
    : "—";
  const kinds = (data.issues || [])
    .map((i) => (i.kind || "").replace(/_/g, " "))
    .join(", ");
  return (
    <div
      data-testid="brain-health-pill-degraded"
      title={`Open issues: ${kinds || "unknown"} — since ${since}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/[0.10] text-amber-200 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.16em] mb-4"
    >
      <ShieldAlert className="h-2.5 w-2.5" />
      Brain degraded
    </div>
  );
}
